import { useState, useEffect } from 'react'
import { useTitle } from '../../hooks/useTitle'
import api from '../../services/api'
import { deleteInvoice, printInvoice } from '../../services/invoice'
import formatMoney from '../../util/formatMoney'
import getDateStr from '../../util/getDateStr'
import { withApiErrorDetail } from '../../util/getApiErrorMessage'
import * as S from './styles'
import Loading from '../../commons/Loading'
import Breadcrumb from '../../components/Breadcrumb'
import CartEmpty from '../../components/Cart/CartEmpty'
import LoadingBackground from '../../components/LoadingBackground'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'
import { useNavigate } from 'react-router-dom'
import { faCheck } from '@fortawesome/free-solid-svg-icons'

const Orders = () => {
  const [isOrdersLoading, setIsOrdersLoading] = useState(true)
  const [reloadInvoices, setReloadInvoices] = useState(false)
  const [invoices, setInvoices] = useState([])
  const navigate = useNavigate()

  const getInvoices = async () => {
    const response = await api.get('/Invoice')
    setInvoices(response.data)
    setIsOrdersLoading(false)
    ReactTooltip.rebuild()
  }

  useEffect(() => {
    getInvoices()
  }, [reloadInvoices])

  const handleDelete = async (id, status, integrationCode, customerCode) => {
    setIsOrdersLoading(true)
    const deleteResponse = await deleteInvoice(id, status, integrationCode, customerCode)
    setIsOrdersLoading(false)
    if (deleteResponse) getInvoices()
    setIsOrdersLoading(false)
  }

  const handleDuplicateOrder = async (invoiceFromList) => {
    const id = toast.loading(`Duplicando pedido ${invoiceFromList.invoiceId}`, {
      position: 'top-right',
    })
    setIsOrdersLoading(true)
    try {
      const responseInvoice = await api.get(`/Invoice/${invoiceFromList.invoiceId}`)
      const invoice = responseInvoice.data
      const items = invoice.items || []
      items.forEach(el => { el.id = 0 })
      const dataCalculate = {
        Advance: invoice.advance,
        DeadlineId: invoice.deadlineId,
        CarrierId: invoice.carrierId,
        DeliveryForecast: invoice.deliveryForecast,
        Discount: invoice.discount,
        Freight: invoice.freight,
        Items: items,
        Nature: invoice.nature,
        Notes: invoice.notes,
        PaymentMethodId: invoice.paymentMethodId,
        Price: null,
        Tax: invoice.tax,
        TotalValue: invoice.totalValue,
        Value: invoice.value,
        CreationSource: 2,
        TaxIpi: invoice.taxIpi,
        TaxSt: invoice.taxSt,
      }
      const responseCalculate = await api.post('/Invoice/Calculate', dataCalculate)

      const data = {
        Advance: responseCalculate.data.data.advance,
        DeadlineId: responseCalculate.data.data.deadlineId,
        CarrierId: responseCalculate.data.data.carrierId,
        DeliveryForecast: responseCalculate.data.data.deliveryForecast,
        Discount: responseCalculate.data.data.discount,
        Freight: responseCalculate.data.data.freight,
        Items: responseCalculate.data.data.items,
        Nature: responseCalculate.data.data.nature,
        Notes: responseCalculate.data.data.notes,
        PaymentMethodId: responseCalculate.data.data.paymentMethodId,
        Price: null,
        Tax: responseCalculate.data.data.tax,
        TotalValue: responseCalculate.data.data.totalValue,
        Value: responseCalculate.data.data.value,
        CreationSource: 2,
        TaxIpi: responseCalculate.data.data.taxIpi,
        TaxSt: responseCalculate.data.data.taxSt,
      }

      const response = await api.post('/Invoice', data)
      if (response.data.success) {
        toast.update(id, {
          render: response.data.successMessages[0],
          type: 'success',
          isLoading: false,
          delay: 750,
          autoClose: 2000,
        })
        const warningMessage = response.data.warningMessages?.join('<br>')
        if (warningMessage) {
          toast.update(id, {
            render: warningMessage,
            type: 'warning',
            isLoading: false,
            delay: 750,
            autoClose: 3000,
          })
          setTimeout(() => navigate(`/orders/${response.data.data.invoiceId}`, { state: { editing: false } }), 3000)
        } else {
          navigate(`/orders/${response.data.data.invoiceId}`, { state: { editing: false } })
        }
      }
    } catch (err) {
      toast.update(id, {
        render: withApiErrorDetail(err, 'Erro ao duplicar pedido'),
        type: 'error',
        isLoading: false,
        delay: 750,
        autoClose: 10000,
      })
    }

    setIsOrdersLoading(false)
  }

  const handleConfirmOrder = async (invoice) => {
    const id = toast.loading(`Aprovando pedido ${invoice.invoiceId}`, {
      position: 'top-right',
    })
    setIsOrdersLoading(true)
    
    try {
      const response = await api.put(`/Invoice/Confirm/${invoice.invoiceId}`)
      if (response.data.success) {
        toast.update(id, {
          render: response.data.successMessages[0],
          type: 'success',
          isLoading: false,
          delay: 750,
          autoClose: 2000,
        })
        setReloadInvoices(!reloadInvoices)
        // navigate(`/orders/${invoice.invoiceId}`, { state: { editing: false } })
      }
    } catch (err) {
      toast.update(id, {
        render: 'Erro ao aprovar pedido',
        type: 'error',
        isLoading: false,
        delay: 750,
        autoClose: 2000,
      })
    }

    setIsOrdersLoading(false)
  }

  const handlePrint = async (id) => {
    setIsOrdersLoading(true)
    await printInvoice(id)
    setIsOrdersLoading(false)
  }

  useTitle('Pedidos')

  return (
    <S.Orders>
      <Breadcrumb current='Meus Pedidos' />

      {isOrdersLoading
        ? (
          <LoadingBackground />
          )
        : (
          <>
            {invoices.length > 0
              ? (
                <>
                  <S.OrdersTop>
                    <S.OrdersTitle>Meus Pedidos</S.OrdersTitle>
                    {invoices.length > 0
                      ? <S.OrdersQty>{invoices.length}</S.OrdersQty>
                      : <Loading />}
                  </S.OrdersTop>

                  {invoices.length > 0 && (
                    <S.OrdersTable>
                      <S.OrdersTableHeader>
                        <S.OrdersTableItem>ID</S.OrdersTableItem>
                        <S.OrdersTableItem>Data</S.OrdersTableItem>
                        <S.OrdersTableItem>Status</S.OrdersTableItem>
                        <S.OrdersTableItem>Valor</S.OrdersTableItem>
                        <S.OrdersTableItem>Observações</S.OrdersTableItem>
                        <S.OrdersTableItem>Ações</S.OrdersTableItem>
                      </S.OrdersTableHeader>

                      {invoices.map(invoice => (
                        <S.OrdersTableRow key={invoice.invoiceId}>
                          <S.OrdersTableItem>
                            <S.OrdersTableItemLinkId to={`/orders/${invoice.invoiceId}`}>
                              {invoice.invoiceId}
                            </S.OrdersTableItemLinkId>
                          </S.OrdersTableItem>
                          <S.OrdersTableItem>{getDateStr(invoice.timeCreation)}</S.OrdersTableItem>
                          <S.OrdersTableItem>{invoice.statusDescription + (invoice.integrationCode != null ? ' (Integrado)' : '')}</S.OrdersTableItem>
                          <S.OrdersTableItem>{formatMoney(invoice.totalValue)}</S.OrdersTableItem>
                          <S.OrdersTableItem>{invoice.notes}</S.OrdersTableItem>
                          <S.OrdersTableItem>
                            <S.OrdersTableItemLink to={`/orders/${invoice.invoiceId}`}>
                              <S.OrdersIcon type='circledInfo' />
                              Ver detalhes
                            </S.OrdersTableItemLink>
                          </S.OrdersTableItem>
                          <S.OrdersTableItem>
                            {
                              invoice.status == 7
                              ? (
                                  <S.OrdersButton onClick={() => handleConfirmOrder(invoice)} data-tip='Aprovar'>
                                    <S.StyledFontAwesomeIcon icon={faCheck} />
                                  </S.OrdersButton>
                              )
                              : (
                                  <S.OrdersButton onClick={() => handleDuplicateOrder(invoice)} data-tip='Duplicar'>
                                    <S.OrdersIcon type='inboxes' />
                                  </S.OrdersButton>
                              )
                            }
                          </S.OrdersTableItem>
                          <S.OrdersTableItem>
                            <S.OrdersButton onClick={() => handleDelete(invoice.invoiceId, invoice.status, invoice.integrationCode, invoice.customerCode)} disabled={isOrdersLoading} data-tip='Deletar'>
                              <S.OrdersIcon type='trash' />
                            </S.OrdersButton>
                          </S.OrdersTableItem>
                          <S.OrdersTableItem>
                            <S.OrdersButton data-tip='Imprimir' onClick={() => handlePrint(invoice.invoiceId)}>
                              <S.OrdersIcon type='print' />
                            </S.OrdersButton>
                          </S.OrdersTableItem>
                        </S.OrdersTableRow>
                      ))}
                    </S.OrdersTable>
                  )}
                  {invoices.length > 0 && (
                    <S.OrdersTableMobile>
                      {invoices.map(invoice => (
                        <span key={invoice.invoiceId}>
                          <S.OrdersTableHeader>
                            <S.OrdersTableItem>ID</S.OrdersTableItem>
                            <S.OrdersTableItem>Data</S.OrdersTableItem>
                            <S.OrdersTableItem>Status</S.OrdersTableItem>
                          </S.OrdersTableHeader>
                          <S.OrdersTableRow>
                            <S.OrdersTableItem>
                              <S.OrdersTableItemLinkId to={`/orders/${invoice.invoiceId}`}>
                                {invoice.invoiceId}
                              </S.OrdersTableItemLinkId>
                            </S.OrdersTableItem>
                            <S.OrdersTableItem>{getDateStr(invoice.timeCreation)}</S.OrdersTableItem>
                            <S.OrdersTableItem>{invoice.statusDescription}</S.OrdersTableItem>
                            <S.OrdersTableItem>
                              <S.OrdersTableItemLink to={`/orders/${invoice.invoiceId}`}>
                                <S.OrdersIcon type='circledInfo' />
                                Ver detalhes
                              </S.OrdersTableItemLink>
                              <S.OrdersTableItemLink to='/orders/' onClick={() => handleDuplicateOrder(invoice)}>
                                <S.OrdersIcon type='inboxes' />
                                Duplicar Pedido
                              </S.OrdersTableItemLink>
                            </S.OrdersTableItem>
                          </S.OrdersTableRow>
                        </span>
                      ))}
                    </S.OrdersTableMobile>
                  )}
                </>
                )
              : (
                <CartEmpty>
                  Parece que você ainda não tem nenhum pedido 😱 <br />
                  <strong>Vamos mudar isso?</strong>
                </CartEmpty>
                )}
          </>
          )}

    </S.Orders>

  )
}

export default Orders
